// apps/ui/src/niches/NicheLandingPage.js
// Programmatic SEO Landing Page Component for Retail & eCommerce Niches
// Dynamically rendered at /create/:slug (e.g. /create/clothing-website, /create/jewelry-website, /create/phone-cases-website)

import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../components/Navbar';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import PreviewImage from '../components/PreviewImage';
import CustomWebsiteModal from '../components/CustomWebsiteModal';
import { heroTemplates, demoStoreUrl } from '../components/Hero';
import useDemoThemes from '../utils/demoThemes';
import { useOwnezzyContact, whatsappLink } from '../legal/contact';
import { getNicheBySlug, NICHES } from './nicheData';
import { hideSiteFaq } from '../utils/siteFaq';
import './NicheLandingPage.css';
import SeoArticle from '../components/SeoArticle';
import { nicheArticle } from '../seo/seoContent';

const PREVIEW_SIZES = '(max-width: 575px) calc(100vw - 58px), 480px';

export default function NicheLandingPage({ slug }) {
  const contact = useOwnezzyContact();
  const templates = useDemoThemes(heroTemplates);
  const niche = useMemo(() => getNicheBySlug(slug), [slug]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Find corresponding template from heroTemplates
  const matchingTemplate = useMemo(() => {
    return templates.find((t) => t.id === niche.templateId) || templates[0];
  }, [templates, niche.templateId]);

  const demoUrl = demoStoreUrl(matchingTemplate.demo);

  // Dynamic SEO Meta Tags & Schema Injection
  useEffect(() => {
    document.title = niche.metaTitle;
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };

    const canonicalUrl = `https://ownezzy.com/create/${niche.slug}`;
    setMeta('meta[name="description"]', 'content', niche.metaDescription);
    setMeta('link[rel="canonical"]', 'href', canonicalUrl);
    setMeta('meta[property="og:title"]', 'content', niche.metaTitle);
    setMeta('meta[property="og:description"]', 'content', niche.metaDescription);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[name="keywords"]', 'content', niche.targetKeywords.join(', '));

    // Inject FAQ Schema JSON-LD
    const faqSchemaScript = document.createElement('script');
    faqSchemaScript.type = 'application/ld+json';
    faqSchemaScript.id = 'niche-faq-schema';
    faqSchemaScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: niche.faqList.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a
        }
      }))
    });

    const prevScript = document.getElementById('niche-faq-schema');
    if (prevScript) prevScript.remove();
    const restoreSiteFaq = hideSiteFaq();
    document.head.appendChild(faqSchemaScript);

    window.scrollTo(0, 0);

    return () => {
      const el = document.getElementById('niche-faq-schema');
      if (el) el.remove();
      restoreSiteFaq();
    };
  }, [niche]);

  const toggleFaq = (idx) => {
    setOpenFaqIndex((prev) => (prev === idx ? null : idx));
  };

  return (
    <div className="niche-page-wrapper">
      <AnnouncementBar />
      <Navbar />

      {/* Hero Section */}
      <section className="niche-hero-section" aria-label="Hero">
        <div className="niche-hero-container">
          <div className="niche-hero-content">
            <div className="niche-badge-pill">
              <span className="niche-badge-dot" />
              <span>{niche.badge}</span>
            </div>

            <h1 className="niche-hero-title">{niche.headline}</h1>
            <p className="niche-hero-sub">{niche.subheadline}</p>

            <div className="niche-hero-ctas">
              <button
                type="button"
                className="niche-primary-btn"
                onClick={() => setIsModalOpen(true)}
              >
                <span>Launch {niche.shortName} Website Free</span>
                <span>→</span>
              </button>

              <a
                href={demoUrl}
                target="_blank"
                rel="noreferrer"
                className="niche-secondary-btn"
              >
                <span>Live Demo Store</span>
                <span>↗</span>
              </a>

              <a
                href={whatsappLink(contact, `Hi Ownezzy! I want to create a website for my ${niche.shortName} business. Can you guide me?`)}
                target="_blank"
                rel="noreferrer"
                className="niche-wa-btn"
              >
                <span>Chat on WhatsApp</span>
              </a>
            </div>

            <div className="niche-stats-row">
              <div className="niche-stat-item">
                <span className="niche-stat-num">{niche.heroStats.activeStores}</span>
                <span className="niche-stat-label">Stores Created</span>
              </div>
              <div className="niche-stat-item">
                <span className="niche-stat-num">{niche.heroStats.avgSetupTime}</span>
                <span className="niche-stat-label">Setup Time</span>
              </div>
              <div className="niche-stat-item">
                <span className="niche-stat-num">{niche.heroStats.commission}</span>
                <span className="niche-stat-label">Commission</span>
              </div>
              <div className="niche-stat-item">
                <span className="niche-stat-num">{niche.heroStats.rating}</span>
                <span className="niche-stat-label">Customer Rating</span>
              </div>
            </div>
          </div>

          {/* Hero Showcase Mockup */}
          <div className="niche-hero-mockup-wrapper">
            <div className="niche-mockup-card">
              <div className="niche-browser-bar">
                <div className="niche-browser-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="niche-browser-url">
                  {matchingTemplate.demo}.ownezzy.com
                </div>
                <div style={{ width: 40 }} />
              </div>

              <div className="niche-mockup-img-container">
                <PreviewImage
                  template={matchingTemplate}
                  sizes={PREVIEW_SIZES}
                  alt={`${niche.nicheName} ready-made online website demo`}
                />
                <div className="niche-mockup-badge">
                  <span>● Live Preview</span>
                </div>
              </div>

              <div className="niche-mockup-footer">
                <div className="niche-mockup-store-info">
                  <h4>{matchingTemplate.name}</h4>
                  <p>{niche.nicheName}</p>
                </div>
                <a
                  href={demoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="niche-secondary-btn"
                  style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                >
                  Test Order ↗
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits tailored to this Niche */}
      <section className="niche-section">
        <div className="niche-section-header">
          <span className="niche-section-tag">Designed for Your Products</span>
          <h2 className="niche-section-title">Why Sell {niche.shortName} Online with Ownezzy?</h2>
          <p className="niche-section-sub">
            Built specifically to solve the real hurdles Indian {niche.shortName.toLowerCase()} sellers face every day.
          </p>
        </div>

        <div className="niche-benefits-grid">
          {niche.keyBenefits.map((b, idx) => (
            <div key={idx} className="niche-benefit-card">
              <span className="niche-benefit-icon">{b.icon}</span>
              <h3>{b.title}</h3>
              <p>{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Features Checklist */}
        <div className="niche-features-checklist">
          {niche.featuresList.map((f, idx) => (
            <div key={idx} className="niche-feature-item">
              <span className="niche-check-icon">✓</span>
              <span>{f}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3-Step Setup Section */}
      <div className="niche-steps-bg">
        <section className="niche-section">
          <div className="niche-section-header">
            <span className="niche-section-tag">Zero Technical Knowledge Required</span>
            <h2 className="niche-section-title">Launch Your {niche.shortName} Website in 3 Steps</h2>
            <p className="niche-section-sub">
              No developer needed. If you know how to use WhatsApp, you can run this store.
            </p>
          </div>

          <div className="niche-steps-grid">
            {niche.howToSteps.map((s, idx) => (
              <div key={idx} className="niche-step-card">
                <div className="niche-step-number">{s.step}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* FAQ Section */}
      <section className="niche-section">
        <div className="niche-section-header">
          <span className="niche-section-tag">Frequently Asked Questions</span>
          <h2 className="niche-section-title">{niche.shortName} Store Owners Often Ask</h2>
          <p className="niche-section-sub">
            Everything you need to know about payments, shipping, domains, and getting started.
          </p>
        </div>

        <div className="niche-faq-list">
          {niche.faqList.map((faq, idx) => {
            const isOpen = openFaqIndex === idx;
            return (
              <div key={idx} className="niche-faq-card">
                <button
                  type="button"
                  className="niche-faq-question"
                  onClick={() => toggleFaq(idx)}
                  aria-expanded={isOpen}
                >
                  <span>{faq.q}</span>
                  <span>{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && <div className="niche-faq-answer">{faq.a}</div>}
              </div>
            );
          })}
        </div>
      </section>

      {/* Related Niches Grid (Internal Linking Hub) */}
      <section className="niche-section">
        <div className="niche-section-header">
          <span className="niche-section-tag">Explore All Niches</span>
          <h2 className="niche-section-title">Create Websites for Other Businesses</h2>
          <p className="niche-section-sub">
            Ownezzy powers 100+ specialized store themes across India.
          </p>
        </div>

        <div className="niche-related-grid">
          {NICHES.filter((n) => n.slug !== niche.slug).map((n) => (
            <a
              key={n.slug}
              href={`/create/${n.slug}`}
              className="niche-related-card"
            >
              <span className="niche-related-icon">{n.icon}</span>
              <div>
                <strong>{n.nicheName}</strong>
                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  /create/{n.slug}
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <SeoArticle article={nicheArticle(niche)} />

      {/* Final Call to Action Banner */}
      <section className="niche-section" style={{ paddingTop: 0 }}>
        <div className="niche-cta-banner">
          <h2>Ready to Launch Your {niche.shortName} Website?</h2>
          <p>
            Join 2,400+ Indian retail shops and Instagram sellers who sell online with 0% commission on Ownezzy.
          </p>
          <div className="niche-banner-ctas">
            <button
              type="button"
              className="niche-banner-primary"
              onClick={() => setIsModalOpen(true)}
            >
              Start 7-Day Free Trial
            </button>
            <a
              href="/websites"
              className="niche-secondary-btn"
              style={{ background: 'transparent', color: '#ffffff', borderColor: '#475569' }}
            >
              Browse All 100+ Websites
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Customized Website Modal */}
      <CustomWebsiteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialCategory={niche.nicheName}
        initialTemplate={niche.templateId}
      />
    </div>
  );
}
